/**
 * CAMS Air Quality Explorer — Entry Point
 *
 * Orchestrează modulele: data (zarrita) → render (canvas) → map (MapLibre)
 * + timeline (slider) + query (click pixel) + chart (uPlot) + ui
 * + wind (particule animate)
 */
import { loadCube, loadStoreMetadata, timesToDates,
         isWeatherAvailable, loadWindField, getWindSlice,
         camsToWeatherTimeIndex, loadWeatherCube,
         findClosestTimeIndex } from './data.js';
import { initMap, updateRasterLayer, onMapClick, placeCityMarker, setMaskEnabled, isMaskEnabled, loadMaskBoundary } from './map.js';
import { initTimeline, updateTimelineLabels, stopPlay } from './timeline.js';
import { handlePixelClick, updatePixelValue, refreshPixelForVariable, getCurrentPixel } from './query.js';
import { updateLegend, updateRunInfo, hideLoading, showLoading, initUI, showWeatherLayers } from './ui.js';
import { WindParticleSystem } from './wind.js';
import { initCityWidget, updateCityWidget, getSelectedCity } from './cityWidget.js';
import { initForecastWidget } from './forecastWidget.js';

// Crossfilter modules
import { initSQL, loadPopulation, registerCrossfilterTable } from './sqlEngine.js';
import {
  setVariables, onCrossfilterChange, onCrossfilterStart,
  getSelections, hasActiveFilters, refresh as refreshCrossfilter
} from './crossfilter.js';
import { initHistograms } from './histogram.js';
import { initPopulationPanel } from './populationPanel.js';
import { monteazaComutator } from './data-source.js';

// ── State ────────────────────────────────────────────────────────
let currentVariable = 'pm25';
let currentTimeIndex = 0;
let currentCube = null;
let currentDates = null;
let isCurrentWeather = false;  // true dacă variabila curentă e meteo

// Weather state
let windSystem = null;
let windCubes = null;        // { u, v } — cuburi U/V
let windEnabled = false;
let crossfilterReady = false;  // true după init DuckDB + populație
let currentSelectedIds = null;
let showPopMap = false;
const WEATHER_VARS = new Set(['temp2m', 'wind_speed', 'cloud', 'precip']);
const AQ_VARS = new Set(['pm25', 'pm10', 'no2', 'o3', 'so2', 'co']);

// ── Boot ─────────────────────────────────────────────────────────
async function boot() {
  console.log('🌍 CAMS Air Quality Explorer — boot');

  // 1. Inițializare UI
  initUI(handleVariableChange, handleWindToggle);
  // Comutatorul de sursă (CAMS / model național) — vezi data-source.js.
  monteazaComutator(document.getElementById('sursa-date'));
  updateLegend(currentVariable);

  // Mască România toggle
  const maskCheckbox = document.getElementById('mask-ro-checkbox');
  if (maskCheckbox) {
    maskCheckbox.checked = isMaskEnabled();
    maskCheckbox.addEventListener('change', () => {
      setMaskEnabled(maskCheckbox.checked);
      // Re-render rasterul cu/fără clipping
      if (currentCube) {
        updateRasterLayer(currentCube, currentTimeIndex, currentVariable, currentSelectedIds, showPopMap);
      }
      // Reset particulele de vânt pentru noua stare a măștii
      if (windSystem) {
        windSystem._needsReset = true;
      }
    });
  }

  // 2. Inițializare hartă
  const map = await initMap();
  console.log('✓ MapLibre inițializat');

  // 3. Inițializare wind canvas
  const windCanvas = document.getElementById('wind-canvas');
  windSystem = new WindParticleSystem(windCanvas, map);

  // 4. Încarcă granița României (pentru maskă canvas)
  await loadMaskBoundary();

  // 5. Încărcare date
  showLoading();

  try {
    // Metadata store (run_date, etc.)
    const attrs = await loadStoreMetadata();
    if (attrs?.run_date) {
      updateRunInfo(attrs.run_date);
    }

    // Încărcare cub pentru variabila implicită
    currentCube = await loadCube(currentVariable);
    currentDates = timesToDates(currentCube.times, currentCube.timeAttrs);

    console.log(`✓ Cub încărcat: ${currentVariable}`, currentCube.shape);
    console.log(`  Time: ${currentDates[0]?.toISOString()} → ${currentDates[currentDates.length - 1]?.toISOString()}`);

    // Calculează indexul de timp cel mai apropiat de "acum"
    currentTimeIndex = findClosestTimeIndex(currentDates);
    console.log(`  Auto-select timeIndex: ${currentTimeIndex} (${currentDates[currentTimeIndex]?.toISOString()})`);

    // Randare inițială pe hartă
    updateRasterLayer(currentCube, currentTimeIndex, currentVariable, currentSelectedIds, showPopMap);
    updateLegend(currentVariable);
    const maxTime = currentCube.shape[0] - 1;
    initTimeline(maxTime, handleTimeChange, currentTimeIndex);
    updateTimelineLabels(currentTimeIndex, currentDates, isCurrentWeather);

    // Handler click pe hartă
    onMapClick((lng, lat) => {
      handlePixelClick(lng, lat, currentCube, currentVariable, currentTimeIndex);
    });

    // 5. Inițializare widget orașe
    const defaultCity = await initCityWidget();
    updateCityWidget(currentCube, currentVariable, currentTimeIndex, currentDates);

    // Plasează marker pe orașul implicit
    if (defaultCity) {
      placeCityMarker(defaultCity.lon, defaultCity.lat, defaultCity.name);
    }

    // Listen for city selection changes
    document.addEventListener('city-selected', (e) => {
      updateCityWidget(currentCube, currentVariable, currentTimeIndex, currentDates);
      // Actualizează marker-ul de pe hartă
      const city = e.detail?.city || getSelectedCity();
      if (city) {
        placeCityMarker(city.lon, city.lat, city.name);
      }
    });

    // Setup Info Modal
    const infoBtn = document.getElementById('info-btn');
    const infoModal = document.getElementById('info-modal');
    const closeModal = document.getElementById('close-modal');

    if (infoBtn && infoModal && closeModal) {
      infoBtn.addEventListener('click', () => {
        infoModal.classList.remove('hidden');
      });
      closeModal.addEventListener('click', () => {
        infoModal.classList.add('hidden');
      });
      infoModal.addEventListener('click', (e) => {
        if (e.target === infoModal) {
          infoModal.classList.add('hidden');
        }
      });
    }

    hideLoading();
    console.log('✓ Aplicație gata');

    // 6. Inițializare widget prognoză (asincron)
    initForecastWidget();

    // 7. Verificare și încărcare date meteo (asincron, fără blocare)
    initWeather(map);

    // 8. Inițializare crossfilter (asincron, fără blocare)
    initCrossfilterSystem();

  } catch (error) {
    console.error('Eroare la încărcarea datelor:', error);
    hideLoading();

    const loadingEl = document.getElementById('loading-overlay');
    loadingEl.classList.remove('hidden');
    loadingEl.innerHTML = `
      <div style="text-align: center; max-width: 400px;">
        <div style="font-size: 32px; margin-bottom: 12px;">⚠️</div>
        <div style="font-size: 14px; font-weight: 600; margin-bottom: 8px;">
          Nu s-au găsit date Zarr
        </div>
        <div style="font-size: 12px; color: rgba(240,244,248,0.5); line-height: 1.6;">
          Rulați mai întâi pipeline-ul Python:<br/>
          <code style="background: rgba(255,255,255,0.06); padding: 2px 8px; border-radius: 4px; font-size: 11px;">
            cd pipeline && python run_pipeline.py
          </code>
        </div>
      </div>
    `;
  }
}

// ── Weather Init (non-blocking) ──────────────────────────────────
async function initWeather(map) {
  try {
    const hasWeather = await isWeatherAvailable();
    if (!hasWeather) {
      console.log('ℹ️ Date meteo indisponibile');
      return;
    }

    console.log('🌤️ Încărcare date meteo...');

    // Arată opțiunile meteo în layer menu
    showWeatherLayers();

    // Pre-încarcă câmpul de vânt
    windCubes = await loadWindField();
    console.log('✓ Câmp de vânt încărcat:', windCubes.u.shape);

    // Setează câmpul pentru timestep-ul curent
    updateWindField(currentTimeIndex);

  } catch (error) {
    console.warn('Nu s-au putut încărca datele meteo:', error);
  }
}

// ── Wind Control ─────────────────────────────────────────────────
/**
 * Callback pentru toggle-ul overlay de vânt din meniu.
 */
function handleWindToggle(enabled) {
  setWindEnabled(enabled);
}

function setWindEnabled(enabled) {
  windEnabled = enabled;

  if (windEnabled) {
    if (windCubes) {
      updateWindField(currentTimeIndex);
      windSystem.start();
    }
  } else {
    windSystem.stop();
  }
}

function updateWindField(timeIndex) {
  if (!windCubes || !windSystem) return;
  const wxTimeIdx = isCurrentWeather ? timeIndex : camsToWeatherTimeIndex(timeIndex);
  const slice = getWindSlice(windCubes, wxTimeIdx);
  windSystem.setWindField(
    slice.uData, slice.vData,
    slice.lats, slice.lons,
    slice.shape
  );
}

// ── Crossfilter Init (non-blocking) ──────────────────────────────
async function initCrossfilterSystem() {
  try {
    console.log('📊 Inițializare crossfilter...');

    // Toggle button
    const toggleBtn = document.getElementById('crossfilter-toggle');
    const panel = document.getElementById('crossfilter-panel');
    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', () => {
        panel.classList.toggle('collapsed');
      });
    }

    // 1. Init DuckDB-WASM
    await initSQL();

    // 2. Încarcă populația
    await loadPopulation();

    // 3. Variabilele disponibile (doar AQ la prima rulare)
    const aqVars = ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'];
    const vars = AQ_VARS.has(currentVariable) ? aqVars : [currentVariable];
    setVariables(vars);

    // 4. Înregistrează cubul curent
    const cubes = {};
    for (const v of vars) {
      try {
        const cube = await loadCube(v);
        cubes[v] = cube;
      } catch (e) {
        console.warn(`[Crossfilter] Nu s-a putut încărca ${v}:`, e);
      }
    }
    await registerCrossfilterTable(cubes, currentTimeIndex, vars);

    // 5. Inițializează histogramele și panoul de populație
    initHistograms('histogram-container', vars);
    initPopulationPanel('population-panel');

    // 5.1 Listener pentru map masking & badge spinner
    const badgeEl = document.getElementById('crossfilter-badge');

    onCrossfilterStart(detail => {
      if (badgeEl && detail && detail.hasFilter) {
        badgeEl.classList.add('loading');
        badgeEl.innerHTML = `<span class="filter-spinner"></span>`;
      }
    });

    onCrossfilterChange(result => {
      currentSelectedIds = result.selectedIds || null;
      if (currentCube) {
        updateRasterLayer(currentCube, currentTimeIndex, currentVariable, currentSelectedIds, showPopMap);
      }
      if (badgeEl) {
        badgeEl.classList.remove('loading');
        if (result.hasFilter && result.selections) {
          const count = result.selections.size;
          badgeEl.textContent = count > 0 ? `${count}` : '';
        } else {
          badgeEl.textContent = '';
        }
      }
    });

    // 5.2 Listener toggle population
    const popToggle = document.getElementById('show-pop-checkbox');
    if (popToggle) {
      popToggle.addEventListener('change', (e) => {
        showPopMap = e.target.checked;
        if (currentCube) {
          updateRasterLayer(currentCube, currentTimeIndex, currentVariable, currentSelectedIds, showPopMap);
        }
      });
    }

    crossfilterReady = true;
    console.log('✓ Crossfilter gata');

    // 6. Prima execuție
    refreshCrossfilter();

  } catch (error) {
    console.warn('Crossfilter nu a putut fi inițializat:', error);
  }
}

/**
 * Actualizează tabelul crossfilter la schimbarea timestep-ului.
 */
async function updateCrossfilterTime(timeIndex) {
  if (!crossfilterReady) return;

  try {
    const aqVars = ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'];
    const vars = AQ_VARS.has(currentVariable) ? aqVars : [currentVariable];
    
    const cubes = {};
    for (const v of vars) {
      try {
        const cube = await loadCube(v);
        cubes[v] = cube;
      } catch (e) { /* skip */ }
    }
    
    await registerCrossfilterTable(cubes, timeIndex, vars);
    refreshCrossfilter();
  } catch (e) {
    console.warn('[Crossfilter] Eroare la update time:', e);
  }
}

// ── Handlers ─────────────────────────────────────────────────────

/**
 * Schimbare timestep (de la slider sau animație).
 */
function handleTimeChange(timeIndex) {
  currentTimeIndex = timeIndex;

  // Actualizare hartă
  updateRasterLayer(currentCube, timeIndex, currentVariable, currentSelectedIds, showPopMap);

  // Actualizare labels timeline
  updateTimelineLabels(timeIndex, currentDates, isCurrentWeather);

  // Actualizare pixel (dacă e selectat)
  updatePixelValue(currentVariable, timeIndex);

  // Actualizare câmp de vânt (dacă e activ)
  if (windEnabled && windCubes) {
    updateWindField(timeIndex);
  }

  // Actualizare widget orașe
  updateCityWidget(currentCube, currentVariable, timeIndex, currentDates);

  // Actualizare crossfilter (debounced — doar dacă nu e animație rapidă)
  updateCrossfilterTime(timeIndex);
}

/**
 * Schimbare variabilă (din selector).
 */
async function handleVariableChange(variable) {
  stopPlay();
  showLoading();

  currentVariable = variable;
  const wasWeather = isCurrentWeather;
  isCurrentWeather = WEATHER_VARS.has(variable);
  updateLegend(variable);

  // Memorează data calendaristică curentă înainte de schimbare
  const targetDate = currentDates?.[currentTimeIndex] || null;

  try {
    if (AQ_VARS.has(variable)) {
      // Variabilă calitate aer — din store-ul CAMS
      currentCube = await loadCube(variable);
    } else if (isCurrentWeather) {
      // Variabilă meteo — din store-ul weather
      currentCube = await loadWeatherCube(variable);
    }

    currentDates = timesToDates(currentCube.times, currentCube.timeAttrs);
    console.log(`✓ Variabilă schimbată: ${variable}`, currentCube.shape);

    // Actualizare timeline max (AQ: 96 steps, weather: 32 steps)
    const newMax = currentCube.shape[0] - 1;
    const slider = document.getElementById('time-slider');
    slider.max = newMax;

    // Recalculează timeIndex: găsește cel mai apropiat moment în noul cub
    // (crucial la trecerea AQ orar ↔ meteo 3-orar)
    if (targetDate) {
      currentTimeIndex = findClosestDateIndex(currentDates, targetDate);
    } else {
      currentTimeIndex = findClosestTimeIndex(currentDates);
    }
    slider.value = currentTimeIndex;

    updateTimelineLabels(currentTimeIndex, currentDates, isCurrentWeather);

    // Actualizare hartă
    updateRasterLayer(currentCube, currentTimeIndex, variable, currentSelectedIds, showPopMap);

    // Actualizare pixel panel (dacă e deschis)
    if (getCurrentPixel()) {
      refreshPixelForVariable(currentCube, variable, currentTimeIndex);
    }

    // Actualizare widget orașe
    updateCityWidget(currentCube, variable, currentTimeIndex, currentDates);

    hideLoading();
  } catch (error) {
    console.error(`Eroare la încărcarea ${variable}:`, error);
    hideLoading();
  }
}

/**
 * Găsește indexul din dates[] cel mai apropiat de o dată țintă.
 */
function findClosestDateIndex(dates, targetDate) {
  const targetMs = targetDate.getTime();
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < dates.length; i++) {
    const dist = Math.abs(dates[i].getTime() - targetMs);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

// ── Start ────────────────────────────────────────────────────────
boot();
